import { useState, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Search, X, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/utils/formatPrice";
import type { PlayerPriceResult } from "@/lib/playerPricing";
import type { Enums } from "@/integrations/supabase/types";
import BookingAddonsStep, { type AddonSelection } from "../BookingAddonsStep";
import PriceSummary from "./PriceSummary";
import { useTranslation } from "react-i18next";

interface PlayerProfile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  level: number | null;
  categorySlug?: string;
  categoryName?: string;
}

interface Step2PrivateProps {
  startTime: Date;
  durationMinutes: number;
  onDurationChange: (minutes: number) => void;
  durationSteps: number[];
  isMultiSlotMode: boolean;
  userId: string;
  organizationId: string;
  invitedPlayers: PlayerProfile[];
  onInvitedPlayersChange: (players: PlayerProfile[]) => void;
  splitStrategy: "full" | "per_person";
  onSplitChange: (strategy: "full" | "per_person") => void;
  totalPrice: number;
  courtMaxPlayers: number;
  playerPrices: PlayerPriceResult[];
  addonsTotal: number;
  // Multi-court
  courts: Array<{ courtId: string; courtName: string; courtType?: Enums<"court_type">; maxPlayers?: number }>;
  availableCourts: Array<{ id: string; name: string; type?: Enums<"court_type"> }>;
  onAddCourt?: (courtId: string) => void;
  onRemoveCourt?: (courtId: string) => void;
  // Addons
  addonSelections: AddonSelection[];
  onAddonSelectionsChange: (selections: AddonSelection[]) => void;
}

export default function Step2Private({
  startTime,
  durationMinutes,
  onDurationChange,
  durationSteps,
  isMultiSlotMode,
  userId,
  organizationId,
  invitedPlayers,
  onInvitedPlayersChange,
  splitStrategy,
  onSplitChange,
  totalPrice,
  courtMaxPlayers,
  playerPrices,
  addonsTotal,
  courts,
  availableCourts,
  onAddCourt,
  onRemoveCourt,
  addonSelections,
  onAddonSelectionsChange,
}: Step2PrivateProps) {
  const { t } = useTranslation("booking");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<PlayerProfile[]>([]);
  const [_searching, setSearching] = useState(false);

  // Player search
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      // View enforces profile_visibility at row level — drop the .neq filter
      const { data } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, avatar_url, level")
        .ilike("full_name", `%${searchTerm}%`)
        .neq("id", userId)
        .limit(8);

      if (data && data.length > 0) {
        const { data: customers } = await supabase
          .from("customers")
          .select("user_id, customer_categories(slug, name)")
          .eq("organization_id", organizationId)
          .in("user_id", data.map((d) => d.id));

        const catMap = new Map(
          (customers ?? []).map((c: { user_id: string; customer_categories: { slug: string; name: string } | null }) => [
            c.user_id,
            c.customer_categories,
          ])
        );

        setSearchResults(
          (data as PlayerProfile[]).map((p) => ({
            ...p,
            categorySlug: catMap.get(p.id)?.slug ?? "standard",
            categoryName: catMap.get(p.id)?.name ?? "Standard",
          }))
        );
      } else {
        setSearchResults([]);
      }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, userId, organizationId]);

  const addPlayer = (p: PlayerProfile) => {
    if (!invitedPlayers.find((ip) => ip.id === p.id)) {
      onInvitedPlayersChange([...invitedPlayers, p]);
    }
    setSearchTerm("");
    setSearchResults([]);
  };

  const removePlayer = (id: string) => {
    onInvitedPlayersChange(invitedPlayers.filter((p) => p.id !== id));
  };

  const showSplitChoice = invitedPlayers.length > 0;
  const payFullAmount = totalPrice + addonsTotal;
  const pricePerPerson = Math.ceil(payFullAmount / courtMaxPlayers);

  return (
    <div className="space-y-5 py-4">
      {/* Duration selector -- hidden in multi-slot mode */}
      {!isMultiSlotMode && durationSteps.length > 1 && (
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">
            {t("playTime")}
          </Label>
          <div className="flex flex-wrap gap-2">
            {durationSteps.map((minutes) => (
              <button
                key={minutes}
                type="button"
                onClick={() => onDurationChange(minutes)}
                className={cn(
                  "px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors min-h-[44px]",
                  durationMinutes === minutes
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-muted hover:border-muted-foreground/30"
                )}
              >
                {minutes} min
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border-t pt-4" />

      {/* Player invite */}
      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">
          {t("invitePlayers")}
        </Label>
        {invitedPlayers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {invitedPlayers.map((p) => (
              <Badge key={p.id} variant="secondary" className="gap-1 pr-1">
                {p.full_name ?? t("unknown")}
                {p.categorySlug && p.categorySlug !== "standard" && (
                  <span className="text-[10px] opacity-70">
                    ({p.categoryName})
                  </span>
                )}
                <button
                  onClick={() => removePlayer(p.id)}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("searchByName")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 min-h-[44px]"
          />
        </div>
        {searchResults.length > 0 && (
          <div className="border rounded-md mt-1 max-h-32 overflow-y-auto divide-y">
            {searchResults.map((p) => (
              <button
                key={p.id}
                onClick={() => addPlayer(p)}
                className="w-full flex items-center gap-2 p-2 text-sm hover:bg-muted/50 text-left min-h-[44px]"
              >
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-[10px]">
                    {(p.full_name ?? "?")[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="flex-1 truncate">
                  {p.full_name ?? t("unknown")}
                </span>
                {p.categorySlug && p.categorySlug !== "standard" && (
                  <Badge variant="outline" className="text-[10px] py-0">
                    {p.categoryName}
                  </Badge>
                )}
                {p.level && (
                  <span className="text-xs text-muted-foreground">
                    {t("level", { level: p.level })}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {invitedPlayers.length === 0 && !searchTerm && (
          <p className="text-xs text-muted-foreground mt-1.5">
            {t("noPlayersInvited")}
          </p>
        )}
      </div>

      {/* Add court -- hidden in multi-slot mode */}
      {!isMultiSlotMode && (
        <>
          <div className="border-t pt-4" />

          {/* Multi-court list */}
          {courts.length > 1 && (
            <div className="space-y-2">
              {courts.map((c, i) => (
                <div
                  key={c.courtId}
                  className="flex items-center justify-between rounded-lg bg-muted px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                      {i + 1}
                    </span>
                    <span className="text-sm font-medium">{c.courtName}</span>
                  </div>
                  {i > 0 && onRemoveCourt && (
                    <button
                      onClick={() => onRemoveCourt(c.courtId)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {availableCourts.length > 0 && onAddCourt && (
            <Popover>
              <PopoverTrigger asChild>
                <button className="w-full flex items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-muted-foreground/30 py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors min-h-[44px]">
                  <Plus className="h-4 w-4" />
                  {t("addCourt")}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2">
                {availableCourts.map((ac) => (
                  <button
                    key={ac.id}
                    onClick={() => onAddCourt(ac.id)}
                    className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-muted min-h-[44px]"
                  >
                    {ac.name}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}
        </>
      )}

      {/* Split strategy -- only shown when players are invited */}
      {showSplitChoice && (
        <>
          <div className="border-t pt-4" />
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">
              {t("whoPays")}
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => onSplitChange("full")}
                className={cn(
                  "flex flex-col items-center gap-1 rounded-lg border-2 p-3 text-sm transition-colors min-h-[44px]",
                  splitStrategy === "full"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-muted hover:border-muted-foreground/30"
                )}
              >
                <span className="font-medium text-xs">{t("entireBooking")}</span>
                <span className="text-xs text-muted-foreground">
                  {formatPrice(payFullAmount)}
                </span>
              </button>
              <button
                onClick={() => onSplitChange("per_person")}
                className={cn(
                  "flex flex-col items-center gap-1 rounded-lg border-2 p-3 text-sm transition-colors min-h-[44px]",
                  splitStrategy === "per_person"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-muted hover:border-muted-foreground/30"
                )}
              >
                <span className="font-medium text-xs">{t("eachPerson")}</span>
                <span className="text-xs text-muted-foreground">
                  {formatPrice(pricePerPerson)}/person
                </span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Addons */}
      <BookingAddonsStep
        organizationId={organizationId}
        selections={addonSelections}
        onSelectionsChange={onAddonSelectionsChange}
        bookingStartTime={format(startTime, "HH:mm")}
        bookingDayOfWeek={startTime.getDay() === 0 ? 7 : startTime.getDay()}
      />

      {/* Price breakdown when players invited */}
      {playerPrices.length > 1 && (
        <PriceSummary
          playerPrices={playerPrices}
          currentUserId={userId}
          splitStrategy={splitStrategy}
          totalPrice={totalPrice}
          courtMaxPlayers={courtMaxPlayers}
          isActivity={false}
          addonsTotal={addonsTotal}
        />
      )}
    </div>
  );
}
